import type { SiteConfigFn } from '@wix/thunderbolt-vsm-api'
import type { SeoContextProps } from '../types'
import { CONTEXT_PROPS } from '@wix/advanced-seo-utils/renderer'
import type { HtmlServerSeoPattern } from '@wix/thunderbolt-ssr-api'

const seoSiteConfig: SiteConfigFn<any> = ({ api, funcLib }) => {
	const rendererModel = api.rendererModel
	const publicModel = api.publicModel
	const htmlModel = api.htmlModel
	const originalRequest = api.originalRequest

	const mainPageId = rendererModel.getMainPageId()
	const pagesMap = rendererModel.getPagesMap()
	const mainPage = pagesMap[mainPageId]
	const sitePropertiesInfo = rendererModel.getSitePropertiesInfo()
	const languageLinks = htmlModel.getLanguageLinks()

	// Build context object, filtering out empty values
	const contextBuilder: Partial<SeoContextProps> = {
		[CONTEXT_PROPS.SITE_NAME]: publicModel.getSiteDisplayName() || rendererModel.getSiteInfo()?.siteTitleSEO,
		[CONTEXT_PROPS.SITE_URL]: publicModel.getExternalBaseUrl(),
		[CONTEXT_PROPS.DOMAIN]: publicModel.getDomain(),
		[CONTEXT_PROPS.INDEX_SITE]: publicModel.getIndexable(),
		[CONTEXT_PROPS.DEFAULT_URL]: originalRequest.getUrl(),
		[CONTEXT_PROPS.CURR_LANG_CODE]: '', // Updated in seoSiteFactory from feature-multilingual
		[CONTEXT_PROPS.CURR_LANG_IS_ORIGINAL]: true, // Updated in seoSiteFactory from feature-multilingual
		[CONTEXT_PROPS.CURR_LANG_RESOLUTION_METHOD]: undefined, // Updated in seoSiteFactory from feature-multilingual
		[CONTEXT_PROPS.SITE_OG_IMAGE]: htmlModel.getOgImageUrl(),
		[CONTEXT_PROPS.HOME_PAGE_TITLE]: mainPage?.title,
		[CONTEXT_PROPS.PREV]: htmlModel.getPrevLink(),
		[CONTEXT_PROPS.NEXT]: htmlModel.getNextLink(),
		[CONTEXT_PROPS.FB_ADMINS]: htmlModel.getFacebookAdminId(),
		[CONTEXT_PROPS.BUSINESS_NAME]: sitePropertiesInfo?.businessName,
		[CONTEXT_PROPS.BUSINESS_DESCRIPTION]: sitePropertiesInfo?.description,
		[CONTEXT_PROPS.BUSINESS_LOCALE]: sitePropertiesInfo?.locale,
		[CONTEXT_PROPS.BUSINESS_LOGO]:
			sitePropertiesInfo?.logo && funcLib ? funcLib.buildOgImageFullUrl(sitePropertiesInfo.logo) : undefined,
		[CONTEXT_PROPS.BUSINESS_LOCATION_COUNTRY]: sitePropertiesInfo?.address?.country,
		[CONTEXT_PROPS.BUSINESS_LOCATION_FORMATTED]: sitePropertiesInfo?.address?.googleFormattedAddress,
		[CONTEXT_PROPS.BUSINESS_LOCATION_STATE]: sitePropertiesInfo?.address?.state,
		[CONTEXT_PROPS.BUSINESS_LOCATION_CITY]: sitePropertiesInfo?.address?.city,
		[CONTEXT_PROPS.BUSINESS_LOCATION_STREET]: sitePropertiesInfo?.address?.street,
		[CONTEXT_PROPS.BUSINESS_LOCATION_STREET_NUMBER]: sitePropertiesInfo?.address?.streetNumber,
		[CONTEXT_PROPS.BUSINESS_LOCATION_DESCRIPTION]: sitePropertiesInfo?.address?.hint?.text,
		[CONTEXT_PROPS.BUSINESS_POSTAL_CODE]: sitePropertiesInfo?.address?.zip,
		[CONTEXT_PROPS.BUSINESS_LOCATION_COORDINATES]: sitePropertiesInfo?.address?.coordinates,
		[CONTEXT_PROPS.BUSINESS_SCHEDULE]: sitePropertiesInfo?.businessSchedule,
		[CONTEXT_PROPS.CURRENCY]: sitePropertiesInfo?.currency,
		[CONTEXT_PROPS.EXPERIMENTS]: rendererModel.getSeoExperiments(),
		[CONTEXT_PROPS.PLATFORM_APPS_EXPERIMENTS]: rendererModel.getPlatformAppsExperiments(),
		[CONTEXT_PROPS.SITE_LANGUAGES]: languageLinks?.length ? languageLinks : undefined,
		businessPhone: sitePropertiesInfo?.phone || rendererModel.getSiteMetaData().contactInfo?.phone,
	}

	// Filter out empty values (like Carmi's filterBy)
	const context: SeoContextProps = Object.fromEntries(
		Object.entries(contextBuilder).filter(([, value]) => value !== '' && value !== undefined && value !== null)
	) as SeoContextProps

	// Filter meta tags to exclude specific ones
	// Carmi returns '' for empty/undefined metaTags, array otherwise
	const rawMetaTags = htmlModel.getMetaTags()
	const excludedNames = ['og:description', 'description', 'title']
	const metaTags =
		!rawMetaTags || rawMetaTags.length === 0 ? '' : rawMetaTags.filter((tag) => !excludedNames.includes(tag.name))

	// Carmi returns undefined for missing seoPatterns
	const userPatterns = publicModel.getSeoPatterns() as Array<HtmlServerSeoPattern>

	return {
		shouldLoadSiteFeature: true,
		shouldLoadSiteConfig: true,
		siteConfig: {
			context,
			userPatterns,
			metaTags: metaTags,
			customHeadTags: htmlModel.getCustomHeadTags() ?? '',
			isInSEO: rendererModel.getSeo(),
			hasBlogAmp: publicModel.getHasBlogAmp() ?? false,
			mainPageId,
		},
	}
}

const name = 'seo'

export const config = { site: seoSiteConfig, name }
